import type { FeatureItem } from '../types/features.types'
import { ElitePlansIcon } from '../icons/iconRegistry'
import { ChevronRightIcon } from '../icons/ChevronRightIcon'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { BenefitsInfoBottomSheet } from '../../new-elite-landing/components/NewEliteLandingPageBenefits'
import {
  getElitePlansBenefitSheetConfig,
  resolveFeatureBenefitTabTitle,
  type ElitePlansBenefitSheetVariant
} from '../elitePlansFeatureBenefits'

export type ElitePlansFeatureGridConfig = {
  titleLine1?: string
  titleLine2?: string
  features: FeatureItem[]
  planId: string
  benefitSheetVariant?: ElitePlansBenefitSheetVariant
}

export function ElitePlansFeatureGrid({ config }: { config: ElitePlansFeatureGridConfig }) {
  const {
    titleLine1,
    titleLine2,
    features,
    planId,
    benefitSheetVariant = 'elite'
  } = config
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const { isUserElite, isUserElitePrime } = useElitePlansLanding()

  if (!features.length) return null

  const hasTitle = Boolean(titleLine1)
  const benefitSheetConfig = getElitePlansBenefitSheetConfig(benefitSheetVariant, {
    isUserElite,
    isUserElitePrime
  })

  const handleFeatureOpen = (feature: FeatureItem) => {
    trackElitePlansClick(ElitePlansEventId.FeatureOpen, {
      planId,
      featureId: feature.id,
      buttonLabel: feature.title
    })

    const tabTitle = resolveFeatureBenefitTabTitle(feature.id, benefitSheetVariant)
    if (!tabTitle) {
      return
    }

    openBottomSheet(
      <BenefitsInfoBottomSheet
        onDismiss={closeBottomSheet}
        initialTabTitle={tabTitle}
        tabTitles={benefitSheetConfig.tabTitles}
        sheetTitle={benefitSheetConfig.sheetTitle}
        benefitInfoMap={benefitSheetConfig.contentMap}
      />
    )
  }

  return (
    <section className='px-5'>
      {hasTitle ? (
        <h2 className='text-lg leading-[1.3]'>
          <span className='block font-medium text-black'>{titleLine1}</span>
          {titleLine2 ? (
            <span className='block font-normal text-[#6A6E6E]'>{titleLine2}</span>
          ) : null}
        </h2>
      ) : null}
      <ul
        className={`grid list-none grid-cols-2 gap-x-8 gap-y-6 p-0 md:grid-cols-3 lg:grid-cols-4 ${hasTitle ? 'mt-8' : ''
          }`}
      >
        {features.map((feature) => (
          <ElitePlansFeatureCell
            key={feature.id}
            feature={feature}
            onOpen={() => handleFeatureOpen(feature)}
          />
        ))}
      </ul>
    </section>
  )
}

export function ElitePlansFeatureCell({
  feature,
  onOpen
}: {
  feature: FeatureItem
  onOpen: () => void
}) {
  return (
    <li>
      <button
        type='button'
        className='flex min-h-[88px] w-full flex-col items-start gap-2 border-none bg-transparent p-0 text-left'
        onClick={onOpen}
      >
        <ElitePlansFeatureIcon feature={feature} />
        <span className='text-sm font-medium text-black'>
          {feature.comingSoon ? (
            <span className='mb-1 block text-xs font-medium leading-none text-prime50'>
              Coming soon
            </span>
          ) : null}
          {feature.title}
          <ChevronRightIcon
            size={10}
            color='#232323'
            className='ml-1 mb-[2px] inline-block align-middle'
          />
        </span>
        <span className='text-[12px] font-normal leading-[1.4] text-grey50'>
          {feature.description}
        </span>
      </button>
    </li>
  )
}

export function ElitePlansFeatureIcon({ feature }: { feature: FeatureItem }) {
  if (feature.iconSrc) {
    return (
      <img
        src={feature.iconSrc}
        alt=''
        width={26}
        height={26}
        className='h-[26px] w-[26px] object-contain'
        aria-hidden='true'
      />
    )
  }

  if (feature.iconKey) {
    return (
      <span className='text-prime60' aria-hidden='true'>
        <ElitePlansIcon iconKey={feature.iconKey} width={28} height={28} />
      </span>
    )
  }

  return null
}
